import { useEffect, useRef, useState } from "react";

import KeyboardIcon from "@mui/icons-material/Keyboard";
import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useAuthStore } from "@/store/authStore";

const JUMPS: Record<string, { to: string; label: string }> = {
  h: { to: "/my-home", label: "shortcuts.jumpHome" },
  p: { to: "/planning", label: "shortcuts.jumpPlanning" },
  m: { to: "/my-planning", label: "shortcuts.jumpMyPlanning" },
  d: { to: "/directory", label: "shortcuts.jumpDirectory" },
  o: { to: "/org-health", label: "shortcuts.jumpOrgHealth" },
  a: { to: "/audit", label: "shortcuts.jumpAudit" },
  s: { to: "/settings", label: "shortcuts.jumpSettings" },
};

function isTypingInField(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  if (target.isContentEditable) return true;
  return false;
}

export function KeyboardShortcuts() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const accessToken = useAuthStore((s) => s.accessToken);
  const [open, setOpen] = useState(false);
  const gPendingRef = useRef<NodeJS.Timeout | null>(null);
  const awaitingJumpRef = useRef(false);

  useEffect(() => {
    if (!accessToken) return;

    const onKey = (e: KeyboardEvent) => {
      // Don't hijack typing in any input
      if (isTypingInField(e.target)) return;
      // Ignore modifier combos (Cmd-K palette owns Cmd; others are app-level)
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      // Help / cheatsheet — Shift-? on most layouts
      if (e.key === "?" && !awaitingJumpRef.current) {
        e.preventDefault();
        setOpen(true);
        return;
      }

      // Two-key jump sequences (g + h/p/m/d/o/a/s)
      if (awaitingJumpRef.current) {
        awaitingJumpRef.current = false;
        if (gPendingRef.current) {
          clearTimeout(gPendingRef.current);
          gPendingRef.current = null;
        }
        const dest = JUMPS[e.key.toLowerCase()];
        if (dest) {
          e.preventDefault();
          navigate(dest.to);
        }
        return;
      }
      if (e.key.toLowerCase() === "g") {
        e.preventDefault();
        awaitingJumpRef.current = true;
        gPendingRef.current = setTimeout(() => {
          awaitingJumpRef.current = false;
          gPendingRef.current = null;
        }, 1500);
      }
    };

    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      if (gPendingRef.current) clearTimeout(gPendingRef.current);
    };
  }, [accessToken, navigate]);

  const bindings: { keys: string[]; label: string }[] = [
    { keys: ["⌘", "K"], label: "shortcuts.cmdK" },
    { keys: ["?"], label: "shortcuts.cheatsheet" },
    { keys: ["Esc"], label: "shortcuts.escape" },
    ...Object.entries(JUMPS).map(([k, j]) => ({
      keys: ["g", k.toUpperCase()],
      label: j.label,
    })),
  ];

  return (
    <>
      <Tooltip title={t("shortcuts.fabTooltip")} placement="left">
        <IconButton
          aria-label={t("shortcuts.fabTooltip")}
          onClick={() => setOpen(true)}
          sx={{
            position: "fixed",
            bottom: "calc(88px + env(safe-area-inset-bottom))",
            right: "calc(24px + env(safe-area-inset-right))",
            zIndex: (theme) => theme.zIndex.drawer + 1,
            bgcolor: "background.paper",
            border: 1,
            borderColor: "divider",
            "&:hover": { bgcolor: "action.hover" },
            display: { xs: "none", md: "inline-flex" },
          }}
          size="small"
        >
          <KeyboardIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{t("shortcuts.title")}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t("shortcuts.subtitle")}
          </Typography>
          <Table size="small">
            <TableBody>
              {bindings.map((b) => (
                <TableRow key={b.label}>
                  <TableCell sx={{ width: 160 }}>
                    <Stack direction="row" spacing={0.5}>
                      {b.keys.map((k, i) => (
                        <Box key={i} sx={{ display: "flex", alignItems: "center" }}>
                          <Chip
                            size="small"
                            variant="outlined"
                            label={k}
                            sx={{ fontFamily: "monospace", minWidth: 28 }}
                          />
                          {i < b.keys.length - 1 && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ mx: 0.5 }}
                            >
                              {k === "⌘" || b.keys[0] === "⌘" ? "+" : "→"}
                            </Typography>
                          )}
                        </Box>
                      ))}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{t(b.label)}</Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
      </Dialog>
    </>
  );
}
